(() => {
  const SELECTORS = {
    root: '[data-rseat]',
    productJson: '[data-rseat-product-json]',
    input: '[data-rseat-input]',
    seat: '[data-rseat-seat]',
    form: '[data-rseat-form]',
    submit: '[data-rseat-submit]',
    mobileSubmit: '[data-rseat-mobile-submit]',
    submitCopy: '[data-rseat-submit-copy]',
    count: '[data-rseat-count]',
    countLabel: '[data-rseat-count-label]',
    total: '[data-rseat-total]',
    buttonTotal: '[data-rseat-button-total]',
    empty: '[data-rseat-empty]',
    list: '[data-rseat-selection-list]',
    cartGroup: '[data-rseat-cart-group]',
    cartList: '[data-rseat-cart-list]',
    cartCount: '[data-rseat-cart-count]',
    cartBaseTotal: '[data-rseat-cart-base-total]',
    cartServiceTotal: '[data-rseat-cart-service-total]',
    cartServiceRow: '[data-rseat-cart-service-row]',
    cartTotal: '[data-rseat-cart-total]',
    cartLink: '[data-rseat-cart-link]',
    mobileCartLink: '[data-rseat-mobile-cart-link]',
    overviewCount: '[data-rseat-overview-count]',
    error: '[data-rseat-error]',
    status: '[data-rseat-status]',
    mobileFeedback: '[data-rseat-mobile-feedback]',
    mobileError: '[data-rseat-mobile-error]',
    mobileSummaryToggle: '[data-rseat-mobile-summary-toggle]',
    mobileSeats: '[data-rseat-mobile-seats]',
    mobileOverviewCount: '[data-rseat-mobile-overview-count]',
    mobileOverviewTotal: '[data-rseat-mobile-overview-total]',
    mobileBar: '[data-rseat-mobile-bar]',
    viewport: '[data-rseat-viewport]',
    mapCanvas: '[data-rseat-map-canvas], .rseat-map-canvas',
    mapControl: '[data-rseat-map-control]',
    pan: '.rseat-map-pan',
    zoomOut: '[data-rseat-map-control="zoom-out"]',
    zoomFit: '[data-rseat-map-control="fit"]',
    zoomIn: '[data-rseat-map-control="zoom-in"]',
    minimapWindow: '[data-rseat-minimap-window]',
    availableCount: '[data-rseat-available-count]',
    imageTrigger: '[data-rseat-image-trigger]',
    imageModal: '[data-rseat-image-modal]',
    imageClose: '[data-rseat-image-close]',
  };

  const ROW_ORDER = ['A', 'B', 'C', 'D', 'F', 'G', 'H'];

  class RevibrasSeatSelector {
    constructor(root) {
      this.root = root;
      this.product = this.readProduct();
      if (!this.product) return;

      this.variants = this.product.variants || [];
      this.variantById = new Map(this.variants.map((variant) => [String(variant.id), variant]));
      this.selected = new Map();
      this.cartSeatLines = [];
      this.optimisticCartIds = new Set();
      this.cartSyncVersion = 0;
      this.cartSyncController = null;
      this.cartSyncTimer = null;
      this.feedbackTimer = null;
      this.mapZoom = 1;
      this.mapViewMode = 'initial';
      this.mapNaturalWidth = 0;
      this.lastVisibilitySync = 0;
      this.maxSeats = Math.max(1, Number.parseInt(root.dataset.maxSeats, 10) || 10);
      this.currency = this.product.currency || root.dataset.currency || 'CLP';
      this.money = new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: this.currency,
        maximumFractionDigits: 0,
      });

      this.form = root.querySelector(SELECTORS.form);
      this.submitButton = root.querySelector(SELECTORS.submit);
      this.mobileSubmitButton = root.querySelector(SELECTORS.mobileSubmit);
      this.selectionList = root.querySelector(SELECTORS.list);
      this.cartGroup = root.querySelector(SELECTORS.cartGroup);
      this.cartList = root.querySelector(SELECTORS.cartList);
      this.cartLink = root.querySelector(SELECTORS.cartLink);
      this.mobileCartLink = root.querySelector(SELECTORS.mobileCartLink);
      this.emptyState = root.querySelector(SELECTORS.empty);
      this.errorElement = root.querySelector(SELECTORS.error);
      this.statusElement = root.querySelector(SELECTORS.status);
      this.mobileFeedback = root.querySelector(SELECTORS.mobileFeedback);
      this.mobileError = root.querySelector(SELECTORS.mobileError);
      this.mobileSummaryToggle = root.querySelector(SELECTORS.mobileSummaryToggle);
      this.mobileSeats = root.querySelector(SELECTORS.mobileSeats);
      this.mobileOverviewCount = root.querySelector(SELECTORS.mobileOverviewCount);
      this.mobileOverviewTotal = root.querySelector(SELECTORS.mobileOverviewTotal);
      this.mobileBar = root.querySelector(SELECTORS.mobileBar);
      this.viewport = root.querySelector(SELECTORS.viewport);
      this.mapCanvas = root.querySelector(SELECTORS.mapCanvas);
      this.minimapWindow = root.querySelector(SELECTORS.minimapWindow);
      this.imageTrigger = root.querySelector(SELECTORS.imageTrigger);
      this.imageModal = root.querySelector(SELECTORS.imageModal);
      this.imageClose = root.querySelector(SELECTORS.imageClose);

      this.ensureFeedbackRegions();
      this.bindEvents();
      this.setupSeatNavigation();
      this.setupMapControls();
      this.updateInterface();
      this.initializeMapView();
      this.syncCartSeats(null, { reason: 'initial' });
      this.subscribeToCart();
      this.bindLifecycleEvents();
    }

    readProduct() {
      const element = this.root.querySelector(SELECTORS.productJson);
      if (!element) return null;

      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        console.error('[REVIBRAS Butacas] No se pudo leer la información del producto.', error);
        return null;
      }
    }

    ensureFeedbackRegions() {
      if (!this.statusElement && this.errorElement) {
        this.statusElement = document.createElement('p');
        this.statusElement.className = 'rseat-form-status';
        this.statusElement.dataset.rseatStatus = '';
        this.statusElement.setAttribute('role', 'status');
        this.statusElement.setAttribute('aria-live', 'polite');
        this.statusElement.hidden = true;
        this.errorElement.insertAdjacentElement('afterend', this.statusElement);
      }

      if (!this.mobileFeedback) {
        this.mobileFeedback = document.createElement('div');
        this.mobileFeedback.className = 'rseat-mobile-feedback';
        this.mobileFeedback.dataset.rseatMobileFeedback = '';
        this.mobileFeedback.setAttribute('role', 'status');
        this.mobileFeedback.setAttribute('aria-live', 'polite');
        this.mobileFeedback.hidden = true;
        this.root.appendChild(this.mobileFeedback);
      }
    }

    bindLifecycleEvents() {
      this.handlePageShow = () => this.scheduleCartSync(0, 'pageshow');
      this.handleVisibilityChange = () => {
        if (document.visibilityState !== 'visible') return;
        const now = Date.now();
        if (now - this.lastVisibilitySync < 750) return;
        this.lastVisibilitySync = now;
        this.scheduleCartSync(0, 'visibility');
      };
      this.handleResize = () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.updateMapScrollState(), 100);
      };

      window.addEventListener('pageshow', this.handlePageShow);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('resize', this.handleResize, { passive: true });
    }

    destroy() {
      this.cartSyncController?.abort();
      window.clearTimeout(this.cartSyncTimer);
      window.clearTimeout(this.feedbackTimer);
      window.clearTimeout(this.resizeTimer);
      this.unsubscribe?.();
      window.removeEventListener('pageshow', this.handlePageShow);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.removeEventListener('resize', this.handleResize);
    }

    bindEvents() {
      this.root.querySelectorAll(SELECTORS.input).forEach((input) => {
        const variant = this.variantById.get(String(input.value));
        if (variant) {
          input.setAttribute(
            'aria-label',
            `Butaca ${variant.label}, ${this.formatMoney(variant.price)}${variant.available ? '' : ', no disponible'}`
          );
        }
        input.addEventListener('change', (event) => this.handleSeatChange(event));
        input.addEventListener('focus', () => this.setRovingSeat(input));
        input.addEventListener('keydown', (event) => this.handleSeatKeydown(event));
      });

      this.selectionList?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-rseat-remove]');
        if (!button) return;
        const removeButtons = [...this.selectionList.querySelectorAll('[data-rseat-remove]')];
        this.removeSeat(button.dataset.rseatRemove, {
          returnFocus: true,
          listFocusIndex: removeButtons.indexOf(button),
        });
      });

      this.cartList?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-rseat-cart-remove]');
        if (!button || button.disabled) return;
        const removeButtons = [...this.cartList.querySelectorAll('[data-rseat-cart-remove]:not(:disabled)')];
        void this.removeCartSeat(button.dataset.rseatCartRemove, {
          button,
          listFocusIndex: removeButtons.indexOf(button),
        });
      });

      this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
      this.mobileSummaryToggle?.addEventListener('click', () => this.showMobileSummary());

      this.imageTrigger?.addEventListener('click', () => this.openImageModal());
      this.imageClose?.addEventListener('click', () => this.closeImageModal());
      this.imageModal?.addEventListener('click', (event) => {
        if (event.target === this.imageModal) this.closeImageModal();
      });
      this.imageModal?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeImageModal();
      });

      this.viewport?.addEventListener('scroll', () => this.updateMapScrollState(), { passive: true });
    }

    setupSeatNavigation() {
      this.normalizeSeatDomOrder();
      this.seatInputs = [...this.root.querySelectorAll(SELECTORS.input)];
      if (!this.seatInputs.length) return;

      // La región describe el mapa, pero la única parada de Tab debe ser una
      // butaca. El resto se recorre con flechas, como una cuadrícula nativa.
      if (this.viewport) this.viewport.tabIndex = -1;
      this.refreshRovingSeat();
    }

    normalizeSeatDomOrder() {
      this.root.querySelectorAll('.rseat-row').forEach((row) => {
        const endLabel = row.querySelector('.rseat-row__label--end');
        if (!endLabel) return;
        [...row.querySelectorAll(SELECTORS.seat)]
          .sort((a, b) => {
            const inputA = a.querySelector(SELECTORS.input);
            const inputB = b.querySelector(SELECTORS.input);
            return this.getSeatPosition(inputA).column - this.getSeatPosition(inputB).column;
          })
          .forEach((seat) => row.insertBefore(seat, endLabel));
      });
    }

    getSeatPosition(input) {
      const variant = this.variantById.get(String(input.value));
      const seat = input.closest(SELECTORS.seat);
      const explicitColumn = Number.parseInt(seat?.style.gridColumnStart || seat?.style.gridColumn || '', 10);
      const row = variant?.row || '';
      const rowIndex = ROW_ORDER.indexOf(row);
      let column = Number.isFinite(explicitColumn) ? explicitColumn : Number(variant?.number || 0) + 1;

      if (!Number.isFinite(column)) column = 0;
      return { input, row, rowIndex, column, number: Number(variant?.number || 0) };
    }

    getNavigableSeats() {
      return (this.seatInputs || [])
        .filter((input) => !input.disabled)
        .map((input) => this.getSeatPosition(input))
        .sort((a, b) => a.rowIndex - b.rowIndex || a.column - b.column || a.number - b.number);
    }

    setRovingSeat(input) {
      if (!input || input.disabled) return;
      (this.seatInputs || []).forEach((seatInput) => {
        seatInput.tabIndex = seatInput === input ? 0 : -1;
      });
    }

    refreshRovingSeat(preferredInput = null) {
      const navigable = this.getNavigableSeats();
      if (!navigable.length) {
        (this.seatInputs || []).forEach((input) => {
          input.tabIndex = -1;
        });
        return null;
      }

      const current = preferredInput && !preferredInput.disabled
        ? preferredInput
        : navigable.find(({ input }) => input.tabIndex === 0)?.input;
      const target = current || navigable[0].input;
      this.setRovingSeat(target);
      return target;
    }

    handleSeatKeydown(event) {
      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (!keys.includes(event.key)) return;

      const current = this.getSeatPosition(event.currentTarget);
      const navigable = this.getNavigableSeats();
      let candidates = [];
      let target = null;

      if (event.key === 'Home' || event.key === 'End') {
        candidates = navigable.filter((seat) => seat.rowIndex === current.rowIndex);
        target = event.key === 'Home' ? candidates[0] : candidates[candidates.length - 1];
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        const direction = event.key === 'ArrowLeft' ? -1 : 1;
        candidates = navigable.filter(
          (seat) => seat.rowIndex === current.rowIndex && (seat.column - current.column) * direction > 0
        );
        candidates.sort((a, b) => Math.abs(a.column - current.column) - Math.abs(b.column - current.column));
        target = candidates[0];
      } else {
        const direction = event.key === 'ArrowUp' ? -1 : 1;
        const rowIndexes = [...new Set(
          navigable
            .map((seat) => seat.rowIndex)
            .filter((rowIndex) => (rowIndex - current.rowIndex) * direction > 0)
        )].sort((a, b) => Math.abs(a - current.rowIndex) - Math.abs(b - current.rowIndex));
        const nextRow = rowIndexes[0];
        candidates = navigable.filter((seat) => seat.rowIndex === nextRow);
        candidates.sort((a, b) => Math.abs(a.column - current.column) - Math.abs(b.column - current.column));
        target = candidates[0];
      }

      if (!target) return;
      event.preventDefault();
      this.setRovingSeat(target.input);
      target.input.focus({ preventScroll: true });
      target.input.closest(SELECTORS.seat)?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }

    setupMapControls() {
      this.mapControls = [...this.root.querySelectorAll(SELECTORS.mapControl)];
      this.panControls = [...this.root.querySelectorAll(SELECTORS.pan)];
      this.zoomOutControl = this.root.querySelector(SELECTORS.zoomOut);
      this.zoomFitControl = this.root.querySelector(SELECTORS.zoomFit);
      this.zoomInControl = this.root.querySelector(SELECTORS.zoomIn);

      this.mapControls.forEach((control) => {
        control.addEventListener('click', () => this.handleMapControl(control.dataset.rseatMapControl));
      });

      this.viewport?.addEventListener('wheel', (event) => this.handleMapWheel(event), {
        passive: false,
      });

      if (this.viewport && typeof ResizeObserver === 'function') {
        this.mapResizeObserver = new ResizeObserver(() => {
          if (this.mapViewMode === 'fit') {
            this.setMapZoom(this.getFitMapZoom(), { preserveCenter: false, mode: 'fit' });
          } else {
            this.updateMapScrollState();
          }
        });
        this.mapResizeObserver.observe(this.viewport);
      }

      requestAnimationFrame(() => this.updateMapScrollState());
    }

    handleMapWheel(event) {
      if (!this.viewport || this.viewport.scrollWidth <= this.viewport.clientWidth + 2) return;

      const horizontalIntent = Math.abs(event.deltaX) > Math.abs(event.deltaY);
      const shiftedVertical = event.shiftKey && Math.abs(event.deltaY) > 0;
      if (!horizontalIntent && !shiftedVertical) return;

      const delta = horizontalIntent ? event.deltaX : event.deltaY;
      if (!delta) return;
      event.preventDefault();
      this.viewport.scrollLeft += delta;
    }

    getFitMapZoom() {
      if (!this.viewport || !this.mapCanvas) return 1;
      const rowWidths = [...this.mapCanvas.querySelectorAll('.rseat-row')]
        .map((row) => row.offsetWidth)
        .filter((width) => width > 0);
      const widestRow = rowWidths.length ? Math.max(...rowWidths) + 32 : 0;
      const cssMinWidth = Number.parseFloat(getComputedStyle(this.mapCanvas).minWidth) || 0;
      const naturalWidth = Math.max(1, widestRow, cssMinWidth);
      this.mapNaturalWidth = naturalWidth;
      return Math.min(1, Math.max(0.25, (this.viewport.clientWidth - 2) / naturalWidth));
    }

    handleMapControl(action) {
      if (!this.viewport) return;
      const amount = Math.max(180, this.viewport.clientWidth * 0.72);

      if (action === 'scroll-left' || action === 'scroll-right') {
        this.viewport.scrollBy({
          left: action === 'scroll-left' ? -amount : amount,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
        return;
      }

      if (!this.mapCanvas) return;
      if (action === 'fit') {
        this.setMapZoom(this.getFitMapZoom(), { preserveCenter: false, mode: 'fit' });
      } else if (action === 'zoom-in') {
        this.setMapZoom(this.mapZoom + 0.15, { mode: 'custom' });
      } else if (action === 'zoom-out') {
        this.setMapZoom(this.mapZoom - 0.15, { mode: 'custom' });
      }
    }

    setMapZoom(nextZoom, { preserveCenter = true, mode = this.mapViewMode } = {}) {
      if (!this.mapCanvas || !this.viewport) return;
      const previousWidth = Math.max(1, this.viewport.scrollWidth);
      const centerRatio = (this.viewport.scrollLeft + this.viewport.clientWidth / 2) / previousWidth;
      this.mapZoom = Math.min(1.3, Math.max(0.25, Number(nextZoom.toFixed(3))));
      this.mapViewMode = mode;
      this.mapCanvas.style.zoom = String(this.mapZoom);
      this.root.dataset.rseatMapZoom = String(this.mapZoom);

      requestAnimationFrame(() => {
        const nextWidth = this.viewport.scrollWidth;
        this.viewport.scrollLeft = preserveCenter
          ? Math.max(0, centerRatio * nextWidth - this.viewport.clientWidth / 2)
          : Math.max(0, (nextWidth - this.viewport.clientWidth) / 2);
        this.updateMapScrollState();
      });
    }

    updateMapScrollState() {
      if (!this.viewport) return;
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const atStart = this.viewport.scrollLeft <= 2;
      const atEnd = this.viewport.scrollLeft >= maxScroll - 2;
      const fitZoom = this.getFitMapZoom();
      const showPanControls =
        maxScroll > 2 &&
        this.mapViewMode === 'custom' &&
        this.mapZoom > fitZoom + 0.02;
      this.viewport.classList.toggle('is-at-start', atStart);
      this.viewport.classList.toggle('is-at-end', atEnd);
      this.viewport.classList.toggle('is-scrollable', maxScroll > 2);
      this.root.classList.toggle('is-map-zoomed', showPanControls);

      if (this.minimapWindow) {
        const visibleRatio = Math.min(1, this.viewport.clientWidth / Math.max(1, this.viewport.scrollWidth));
        const travelRatio = maxScroll > 0 ? this.viewport.scrollLeft / maxScroll : 0;
        const minimapWidth = visibleRatio * 100;
        this.minimapWindow.style.width = `${minimapWidth}%`;
        this.minimapWindow.style.left = `${travelRatio * (100 - minimapWidth)}%`;
      }

      this.mapControls?.forEach((control) => {
        const action = control.dataset.rseatMapControl;
        if (action === 'scroll-left') control.disabled = atStart;
        if (action === 'scroll-right') control.disabled = atEnd;
        if (action === 'zoom-out') control.disabled = this.mapZoom <= 0.25;
        if (action === 'zoom-in') control.disabled = this.mapZoom >= 1.3;
      });
      this.panControls?.forEach((control) => {
        control.hidden = !showPanControls;
        control.disabled = control.dataset.rseatMapControl === 'scroll-left' ? atStart : atEnd;
      });
      if (this.zoomOutControl) this.zoomOutControl.disabled = this.mapZoom <= 0.25;
      if (this.zoomInControl) this.zoomInControl.disabled = this.mapZoom >= 1.3;
    }

    showMobileSummary() {
      const summary = this.root.querySelector('.rseat-summary');
      if (!summary) return;
      this.mobileSummaryToggle?.setAttribute('aria-expanded', 'true');
      summary.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
      window.setTimeout(() => this.mobileSummaryToggle?.setAttribute('aria-expanded', 'false'), 500);
    }

    openImageModal() {
      if (!this.imageModal) return;

      this.imageTrigger?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('rseat-image-modal-open');

      if (typeof this.imageModal.showModal === 'function') {
        this.imageModal.showModal();
      } else {
        this.imageModal.hidden = false;
        this.imageModal.setAttribute('open', '');
      }

      this.imageClose?.focus({ preventScroll: true });
    }

    closeImageModal() {
      if (!this.imageModal) return;

      if (typeof this.imageModal.close === 'function' && this.imageModal.open) {
        this.imageModal.close();
      } else {
        this.imageModal.removeAttribute('open');
        this.imageModal.hidden = true;
      }

      this.imageTrigger?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('rseat-image-modal-open');
      this.imageTrigger?.focus({ preventScroll: true });
    }

    initializeMapView() {
      if (!this.viewport || !this.mapCanvas) return;
      requestAnimationFrame(() => {
        this.mapNaturalWidth = 0;
        const isMobile = window.matchMedia('(max-width: 600px)').matches;
        const initialZoom = isMobile ? this.getFitMapZoom() : 0.85;
        this.setMapZoom(initialZoom, {
          preserveCenter: false,
          mode: isMobile ? 'fit' : 'initial',
        });
      });
    }

    handleSeatChange(event) {
      const input = event.currentTarget;
      const id = String(input.value);
      const variant = this.variantById.get(id);
      if (!variant) return;

      this.clearError();
      this.clearStatus();

      if (input.checked) {
        if (this.selected.size >= this.maxSeats) {
          input.checked = false;
          this.showError(this.root.dataset.limitLabel);
          return;
        }
        this.selected.set(id, variant);
      } else {
        this.selected.delete(id);
      }

      this.setRovingSeat(input);
      this.updateInterface();
    }

    removeSeat(id, { returnFocus = false, listFocusIndex = -1 } = {}) {
      const normalizedId = String(id);
      this.selected.delete(normalizedId);

      const input = this.root.querySelector(`${SELECTORS.input}[value="${this.escapeSelector(normalizedId)}"]`);
      if (input) {
        input.checked = false;
        this.setRovingSeat(input);
      }

      this.clearError();
      this.clearStatus();
      this.updateInterface();
      if (returnFocus) {
        const remainingButtons = [...(this.selectionList?.querySelectorAll('[data-rseat-remove]') || [])];
        const nextButton = remainingButtons[Math.min(Math.max(0, listFocusIndex), remainingButtons.length - 1)];
        if (nextButton) {
          nextButton.focus({ preventScroll: true });
        } else if (input) {
          input.focus({ preventScroll: true });
        }
      }
    }

    getSortedSelection() {
      return this.sortSeatEntries([...this.selected.values()]);
    }

    sortSeatEntries(entries) {
      return [...entries].sort((a, b) => {
        const rowDifference = ROW_ORDER.indexOf(a.row) - ROW_ORDER.indexOf(b.row);
        return rowDifference || Number(a.number) - Number(b.number);
      });
    }

    getCartSeatEntries() {
      const confirmedIds = new Set(this.cartSeatLines.map((line) => String(line.variant.id)));
      const optimisticEntries = [...this.optimisticCartIds]
        .filter((id) => !confirmedIds.has(String(id)))
        .map((id) => {
          const variant = this.variantById.get(String(id));
          return variant ? { variant, key: '', quantity: 1, price: variant.price, pending: true } : null;
        })
        .filter(Boolean);

      return this.sortSeatEntries(
        [...this.cartSeatLines, ...optimisticEntries].map((entry) => ({
          ...entry,
          row: entry.variant.row,
          number: entry.variant.number,
        }))
      );
    }

    updateInterface() {
      const variants = this.getSortedSelection();
      const cartEntries = this.getCartSeatEntries();
      const count = variants.length;
      const cartCount = cartEntries.reduce((sum, entry) => sum + Math.max(1, Number(entry.quantity) || 1), 0);
      const overviewCount = count + cartCount;
      const total = variants.reduce((sum, variant) => sum + Number(variant.price || 0), 0);
      const cartTotal = cartEntries.reduce(
        (sum, entry) => sum + Number(entry.price ?? entry.variant.price ?? 0),
        0
      );
      const cartBaseTotal = cartEntries.reduce(
        (sum, entry) => sum + Number(entry.variant.price || 0) * Math.max(1, Number(entry.quantity) || 1),
        0
      );
      const cartServiceTotal = Math.max(0, cartTotal - cartBaseTotal);
      const hasPendingServiceCharge = cartEntries.some((entry) => entry.chargePending);
      const formattedTotal = this.formatMoney(total);
      const hasSelection = count > 0;
      const hasCartSeats = cartCount > 0;

      this.root.classList.toggle('has-selection', hasSelection);
      this.root.classList.toggle('has-cart-seats', hasCartSeats);
      this.root.querySelectorAll(SELECTORS.count).forEach((element) => {
        element.textContent = String(count);
      });
      this.root.querySelectorAll(SELECTORS.countLabel).forEach((element) => {
        element.textContent = String(count);
      });
      this.root.querySelectorAll(SELECTORS.total).forEach((element) => {
        element.textContent = formattedTotal;
      });
      this.root.querySelectorAll(SELECTORS.buttonTotal).forEach((element) => {
        element.textContent = formattedTotal;
      });
      this.root.querySelectorAll(SELECTORS.cartCount).forEach((element) => {
        element.textContent = String(cartCount);
      });
      this.root.querySelectorAll(SELECTORS.cartTotal).forEach((element) => {
        element.textContent = this.formatMoney(cartTotal);
      });
      this.root.querySelectorAll(SELECTORS.cartBaseTotal).forEach((element) => {
        element.textContent = this.formatMoney(cartBaseTotal);
      });
      this.root.querySelectorAll(SELECTORS.cartServiceTotal).forEach((element) => {
        element.textContent = cartServiceTotal > 0 ? `+${this.formatMoney(cartServiceTotal)}` : this.formatMoney(0);
      });
      this.root.querySelectorAll(SELECTORS.cartServiceRow).forEach((element) => {
        element.hidden = cartServiceTotal <= 0 && !hasPendingServiceCharge;
        element.classList.toggle('is-pending', hasPendingServiceCharge);
      });
      this.root.querySelectorAll(SELECTORS.overviewCount).forEach((element) => {
        element.textContent = String(overviewCount);
        element.setAttribute(
          'aria-label',
          `${overviewCount} ${overviewCount === 1 ? 'entrada' : 'entradas'} entre la selección y el carrito`
        );
      });
      if (this.mobileOverviewCount) this.mobileOverviewCount.textContent = String(overviewCount);
      if (this.mobileOverviewTotal) this.mobileOverviewTotal.textContent = this.formatMoney(total + cartTotal);

      const submitCopy = this.root.querySelector(SELECTORS.submitCopy);
      if (submitCopy) {
        submitCopy.textContent = this.root.dataset.buttonLabel;
      }

      if (this.submitButton) this.submitButton.disabled = !hasSelection;
      if (this.mobileSubmitButton) {
        this.mobileSubmitButton.disabled = !hasSelection;
        this.mobileSubmitButton.hidden = !hasSelection;
      }
      if (this.mobileBar) this.mobileBar.hidden = !hasSelection && !hasCartSeats;
      if (this.mobileSeats) {
        const overviewLabels = [
          ...cartEntries.map((entry) => entry.variant.label),
          ...variants.map((variant) => variant.label),
        ];
        this.mobileSeats.textContent = overviewLabels.length
          ? overviewLabels.join(', ')
          : 'VER SELECCIÓN';
      }
      if (this.emptyState) this.emptyState.hidden = hasSelection;
      if (this.cartGroup) this.cartGroup.hidden = !hasCartSeats;
      if (this.cartLink) this.cartLink.hidden = !hasCartSeats;
      if (this.mobileCartLink) this.mobileCartLink.hidden = !hasCartSeats || hasSelection;

      this.renderSelectionList(variants);
      this.renderCartList(cartEntries);
    }

    renderSelectionList(variants) {
      if (!this.selectionList) return;

      const fragment = document.createDocumentFragment();

      variants.forEach((variant) => {
        const item = document.createElement('li');
        const seat = document.createElement('b');
        const detail = document.createElement('span');
        const row = document.createElement('small');
        const price = document.createElement('strong');
        const remove = document.createElement('button');

        seat.textContent = variant.label;
        detail.textContent = `BUTACA ${variant.label}`;
        row.textContent = `FILA ${variant.row}`;
        detail.appendChild(row);
        price.textContent = this.formatMoney(variant.price);
        remove.type = 'button';
        remove.dataset.rseatRemove = variant.id;
        remove.setAttribute('aria-label', `Quitar butaca ${variant.label}`);
        remove.textContent = '×';

        item.append(seat, detail, price, remove);
        fragment.appendChild(item);
      });

      this.selectionList.replaceChildren(fragment);
    }

    renderCartList(entries) {
      if (!this.cartList) return;

      const fragment = document.createDocumentFragment();
      entries.forEach((entry) => {
        const variant = entry.variant;
        const item = document.createElement('li');
        const seat = document.createElement('b');
        const detail = document.createElement('span');
        const row = document.createElement('small');
        const price = document.createElement('div');
        const priceTotal = document.createElement('strong');
        const serviceCharge = document.createElement('small');
        const remove = document.createElement('button');

        item.className = 'is-in-cart';
        seat.textContent = variant.label;
        detail.textContent = `BUTACA ${variant.label}`;
        row.textContent = entry.pending ? 'ACTUALIZANDO CARRITO…' : `FILA ${variant.row} · EN CARRITO`;
        detail.appendChild(row);
        const quantity = Math.max(1, Number(entry.quantity) || 1);
        const baseLinePrice = Number(variant.price || 0) * quantity;
        const finalLinePrice = Number(entry.price ?? baseLinePrice);
        const serviceChargeAmount = Math.max(0, finalLinePrice - baseLinePrice);
        price.className = 'rseat-line-price';
        // La cifra principal es el precio base. El cargo se presenta aparte
        // para que no parezca que se suma nuevamente sobre el total de línea.
        priceTotal.textContent = this.formatMoney(baseLinePrice);
        if (entry.pending || entry.chargePending) {
          serviceCharge.textContent = 'CALCULANDO CARGO…';
          serviceCharge.className = 'is-pending';
        } else if (serviceChargeAmount > 0) {
          serviceCharge.textContent = `+${this.formatMoney(serviceChargeAmount)} CARGO POR SERVICIO`;
        } else {
          serviceCharge.textContent = 'PRECIO DE LA ENTRADA';
        }
        price.append(priceTotal, serviceCharge);
        remove.type = 'button';
        remove.dataset.rseatCartRemove = String(variant.id);
        remove.setAttribute('aria-label', `Quitar butaca ${variant.label} del carrito`);
        remove.textContent = '×';
        remove.disabled = entry.pending;

        item.append(seat, detail, price, remove);
        fragment.appendChild(item);
      });

      this.cartList.replaceChildren(fragment);
    }

    isCompleteCartState(cart) {
      return Array.isArray(cart?.items) && (
        typeof cart.item_count === 'number' ||
        typeof cart.total_price === 'number' ||
        typeof cart.currency === 'string' ||
        typeof cart.token === 'string'
      );
    }

    scheduleCartSync(delay = 80, reason = 'scheduled') {
      window.clearTimeout(this.cartSyncTimer);
      this.cartSyncTimer = window.setTimeout(() => this.syncCartSeats(null, { reason }), delay);
    }

    invalidateCartSync() {
      this.cartSyncVersion += 1;
      this.cartSyncController?.abort();
      this.cartSyncController = null;
    }

    async syncCartSeats(cartState = null, { reason = 'event', serviceChargePendingIds = [] } = {}) {
      const syncVersion = ++this.cartSyncVersion;
      const pendingChargeIds = new Set([...serviceChargePendingIds].map(String));
      let controller = null;

      this.cartSyncController?.abort();
      this.cartSyncController = null;

      try {
        let cart = this.isCompleteCartState(cartState) ? cartState : null;

        if (!cart) {
          controller = new AbortController();
          this.cartSyncController = controller;
          const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
          const response = await fetch(cartUrl, {
            cache: 'no-store',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal: controller.signal,
          });
          if (!response.ok) throw new Error('Cart sync failed');
          cart = await response.json();
        }

        // Sólo la sincronización más reciente puede modificar el mapa. Esto
        // evita que una respuesta iniciada antes de agregar al carrito vuelva
        // a habilitar esas butacas.
        if (syncVersion !== this.cartSyncVersion) return;

        this.cartSeatLines = (cart.items || [])
          .map((item) => {
            const variantId = String(item.variant_id ?? item.id ?? '');
            const variant = this.variantById.get(variantId);
            if (!variant) return null;

            const quantity = Math.max(1, Number(item.quantity) || 1);
            const price = Number(item.final_line_price ?? item.line_price ?? variant.price * quantity);
            return {
              key: String(item.key || variantId),
              variant,
              quantity,
              price,
              serviceCharge: Math.max(0, price - Number(variant.price || 0) * quantity),
              chargePending: pendingChargeIds.has(variantId),
              pending: false,
            };
          })
          .filter(Boolean);
        const cartIds = new Set(this.cartSeatLines.map((line) => String(line.variant.id)));
        // Cualquier carrito completo (respuesta directa o fetch) es la fuente
        // autoritativa. Así una butaca vuelve a habilitarse al eliminarla del
        // drawer, incluso si una actualización secundaria anterior falló.
        this.optimisticCartIds.clear();
        const idsInCart = new Set(cartIds);
        const focusedSeat = document.activeElement?.matches?.(SELECTORS.input) ? document.activeElement : null;
        let removedSelectionCount = 0;

        (this.seatInputs || [...this.root.querySelectorAll(SELECTORS.input)]).forEach((input) => {
          const id = String(input.value);
          const variant = this.variantById.get(id);
          const isInCart = idsInCart.has(id);
          const seatElement = input.closest(SELECTORS.seat);
          const unavailable = !variant?.available;

          input.disabled = unavailable || isInCart;
          input.setAttribute('aria-disabled', String(input.disabled));
          input.setAttribute(
            'aria-label',
            `Butaca ${variant?.label || ''}, ${variant ? this.formatMoney(variant.price) : ''}${
              isInCart ? `, ${this.root.dataset.cartLabel}` : unavailable ? ', no disponible' : ''
            }`
          );
          seatElement?.classList.toggle('is-in-cart', isInCart);

          if ((isInCart || unavailable) && this.selected.has(id)) {
            input.checked = false;
            this.selected.delete(id);
            removedSelectionCount += 1;
          }
        });

        const nextRovingSeat = this.refreshRovingSeat(focusedSeat);
        if (focusedSeat?.disabled && nextRovingSeat) nextRovingSeat.focus({ preventScroll: true });
        this.updateInterface();
        this.updateAvailableCount();

        if (removedSelectionCount > 0 && reason !== 'after-add') {
          this.showStatus(
            removedSelectionCount === 1
              ? 'Actualizamos la disponibilidad: una butaca ya no está libre.'
              : `Actualizamos la disponibilidad: ${removedSelectionCount} butacas ya no están libres.`
          );
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Si el carrito no responde (por ejemplo, en el editor del tema),
          // el mapa conserva el último estado seguro conocido.
          console.warn('[REVIBRAS Butacas] No se pudo actualizar la disponibilidad del carrito.');
          if (reason === 'initial') {
            this.showStatus('No pudimos comprobar el carrito. Reintentaremos automáticamente.');
            this.scheduleCartSync(1500, 'initial-retry');
          }
        }
      } finally {
        if (this.cartSyncController === controller) this.cartSyncController = null;
      }
    }

    subscribeToCart() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event = {}) => {
        // Esta instancia ya marca las butacas inmediatamente después de agregarlas.
        // El Ajax de /cart/add.js no siempre contiene el carrito completo.
        if (event.source === 'revibras-seat-selector') return;

        const cartData = this.isCompleteCartState(event.cartData) ? event.cartData : null;
        return this.syncCartSeats(cartData, { reason: 'cart-event' });
      });
    }

    async requestCartLineRemoval(identifier, sections) {
      const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: identifier,
          quantity: 0,
          sections,
          sections_url: window.location.pathname,
        }),
      });

      let cartState = {};
      try {
        cartState = await response.json();
      } catch (error) {
        cartState = {};
      }

      return { response, cartState };
    }

    async removeCartSeat(variantId, { button = null, listFocusIndex = -1 } = {}) {
      if (!variantId || this.root.classList.contains('is-cart-updating')) return;

      const stableVariantId = String(variantId);
      const entry = this.cartSeatLines.find(
        (line) => String(line.variant.id) === stableVariantId
      );
      if (!entry) {
        this.scheduleCartSync(0, 'missing-cart-line');
        return;
      }

      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const sections = cart?.getSectionsToRender?.().map((section) => section.id).filter(Boolean) || [];
      const originalButtonText = button?.textContent || '×';

      this.invalidateCartSync();
      this.root.classList.add('is-cart-updating');
      this.root.setAttribute('aria-busy', 'true');
      if (this.submitButton) this.submitButton.disabled = true;
      if (this.mobileSubmitButton) this.mobileSubmitButton.disabled = true;
      this.clearError();
      this.clearStatus();
      if (button) {
        button.disabled = true;
        button.textContent = '…';
        button.setAttribute('aria-busy', 'true');
      }

      try {
        let { response, cartState } = await this.requestCartLineRemoval(
          stableVariantId,
          sections
        );

        // Guest Manager puede recalcular el recargo y reemplazar la clave de
        // línea poco después del alta. El ID de variante permanece estable.
        // Si una integración no acepta ese ID, refrescamos la línea y usamos
        // su clave vigente como segundo intento.
        if (!response.ok || cartState.status || cartState.errors) {
          const freshResponse = await fetch(window.routes?.cart_url || '/cart.js', {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          });

          if (freshResponse.ok) {
            const freshCart = await freshResponse.json();
            const currentLine = (freshCart.items || []).find(
              (item) => String(item.variant_id ?? item.id) === stableVariantId
            );

            if (!currentLine) {
              response = freshResponse;
              cartState = freshCart;
            } else {
              ({ response, cartState } = await this.requestCartLineRemoval(
                currentLine.key || stableVariantId,
                sections
              ));
            }
          }
        }

        if (!response.ok || cartState.status || cartState.errors) {
          throw new Error(this.getCartErrorMessage(cartState));
        }

        await this.syncCartSeats(cartState, { reason: 'remove-from-summary' });
        this.updateCartBubble(cartState.sections?.['cart-icon-bubble']);

        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'revibras-seat-selector',
              productVariantId: String(entry.variant.id),
              cartData: cartState,
            });
          }
        } catch (uiError) {
          console.warn('[REVIBRAS Butacas] El carrito cambió, pero no se pudo notificar al tema.', uiError);
        }

        this.showStatus(`Butaca ${entry.variant.label} eliminada del carrito.`, {
          type: 'success',
          timeout: 5000,
        });

        const remainingButtons = [...(this.cartList?.querySelectorAll('[data-rseat-cart-remove]:not(:disabled)') || [])];
        const nextButton = remainingButtons[Math.min(Math.max(0, listFocusIndex), remainingButtons.length - 1)];
        if (nextButton) {
          nextButton.focus({ preventScroll: true });
        } else {
          const seatInput = this.root.querySelector(
            `${SELECTORS.input}[value="${this.escapeSelector(String(entry.variant.id))}"]`
          );
          seatInput?.focus({ preventScroll: true });
        }
      } catch (error) {
        console.error('[REVIBRAS Butacas]', error);
        this.showError(error.message || 'No pudimos quitar la entrada del carrito.');
      } finally {
        this.root.classList.remove('is-cart-updating');
        this.root.setAttribute('aria-busy', 'false');
        if (button?.isConnected) {
          button.disabled = false;
          button.textContent = originalButtonText;
          button.removeAttribute('aria-busy');
        }
        this.updateInterface();
      }
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (
        !this.selected.size ||
        this.root.classList.contains('is-loading') ||
        this.root.classList.contains('is-cart-updating')
      ) return;

      const variants = this.getSortedSelection();
      const items = variants.map((variant) => ({ id: variant.id, quantity: 1 }));
      const addedIds = items.map((item) => String(item.id));
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const sections = cart?.getSectionsToRender?.().map((section) => section.id).filter(Boolean) || [];
      const cartEvent = this.startCartLinesEvent(items);

      this.setLoading(true);
      this.clearError();
      this.clearStatus();
      try {
        cart?.setActiveElement?.(document.activeElement);
      } catch (error) {
        // Algunas versiones de Dawn no exponen setActiveElement.
      }

      try {
        const payload = {
          items,
          sections,
          sections_url: window.location.pathname,
        };
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
        let parsedState = {};
        try {
          parsedState = await response.json();
        } catch (error) {
          if (response.ok) throw new Error(this.root.dataset.errorLabel);
        }

        if (!response.ok || parsedState.status || parsedState.errors) {
          const message = this.getCartErrorMessage(parsedState);
          throw Object.assign(new Error(message), {
            code: 'INVALID',
          });
        }

        // Desde este punto la compra ya fue aceptada por Shopify. El selector
        // mantiene el carrito lateral cerrado para no interrumpir la selección.
        this.invalidateCartSync();
        this.markAddedSeats(addedIds);
        this.showStatus(this.getAddedMessage(variants), { type: 'success', timeout: 6500 });

        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'revibras-seat-selector',
              productVariantId: addedIds[0],
              productVariantIds: addedIds,
              cartData: parsedState,
            });
          }

          this.updateCartBubble(parsedState.sections?.['cart-icon-bubble']);
        } catch (uiError) {
          console.warn('[REVIBRAS Butacas] Las entradas se agregaron, pero no se pudo refrescar el contador.', uiError);
        }

        // La consulta completa se hace en segundo plano para reflejar recargos
        // o cambios que aplique Guest Manager sin recargar la página.
        void this.refreshCartAfterAdd(cartEvent, addedIds);
      } catch (error) {
        if (error.code !== 'INVALID') console.error('[REVIBRAS Butacas]', error);
        this.rejectCartLinesEvent(cartEvent, error);
        this.dispatchCartError(error);
        this.showError(error.message || this.root.dataset.errorLabel);
      } finally {
        this.setLoading(false);
      }
    }

    markAddedSeats(ids) {
      ids.forEach((id) => {
        this.optimisticCartIds.add(String(id));
        const input = this.root.querySelector(`${SELECTORS.input}[value="${this.escapeSelector(id)}"]`);
        if (!input) return;
        input.checked = false;
        input.disabled = true;
        input.setAttribute('aria-disabled', 'true');
        input.closest(SELECTORS.seat)?.classList.add('is-in-cart');
        this.selected.delete(id);
      });
      this.refreshRovingSeat();
      this.updateAvailableCount();
      this.updateInterface();
    }

    updateAvailableCount() {
      const count = [...this.root.querySelectorAll(SELECTORS.input)].filter((input) => !input.disabled).length;
      this.root.querySelectorAll(SELECTORS.availableCount).forEach((element) => {
        element.textContent = String(count);
      });
    }

    updateCartBubble(sectionHtml) {
      if (!sectionHtml) return;
      const target = document.getElementById('cart-icon-bubble');
      const source = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('.shopify-section');
      if (target && source) target.innerHTML = source.innerHTML;
    }

    setLoading(isLoading) {
      this.root.classList.toggle('is-loading', isLoading);
      this.root.setAttribute('aria-busy', String(isLoading));
      this.form?.setAttribute('aria-busy', String(isLoading));
      const submitCopy = this.root.querySelector(SELECTORS.submitCopy);
      if (submitCopy) {
        submitCopy.textContent = isLoading
          ? (this.root.dataset.loadingLabel || 'AGREGANDO…')
          : this.root.dataset.buttonLabel;
      }
      if (this.submitButton) {
        this.submitButton.toggleAttribute('aria-disabled', isLoading);
        this.submitButton.disabled = isLoading || !this.selected.size;
      }
      if (this.mobileSubmitButton) {
        this.mobileSubmitButton.toggleAttribute('aria-disabled', isLoading);
        this.mobileSubmitButton.disabled = isLoading || !this.selected.size;
      }
    }

    showError(message) {
      const conciseMessage = this.getConciseMessage(message || this.root.dataset.errorLabel);
      window.clearTimeout(this.feedbackTimer);
      this.clearStatus();
      if (this.errorElement) {
        this.errorElement.textContent = conciseMessage;
        this.errorElement.hidden = false;
      }
      const mobileErrorTarget = this.mobileError || this.mobileFeedback;
      if (mobileErrorTarget) {
        mobileErrorTarget.textContent = conciseMessage;
        mobileErrorTarget.dataset.type = 'error';
        mobileErrorTarget.setAttribute('role', 'alert');
        mobileErrorTarget.setAttribute('aria-live', 'assertive');
        mobileErrorTarget.hidden = false;
      }
      this.root.dataset.rseatFeedback = 'error';
    }

    clearError() {
      if (this.errorElement) {
        this.errorElement.textContent = '';
        this.errorElement.hidden = true;
      }
      if (this.mobileError) {
        this.mobileError.textContent = '';
        this.mobileError.hidden = true;
        delete this.mobileError.dataset.type;
      }
      if (this.mobileFeedback?.dataset.type === 'error') {
        this.mobileFeedback.textContent = '';
        this.mobileFeedback.hidden = true;
        delete this.mobileFeedback.dataset.type;
      }
      if (this.root.dataset.rseatFeedback === 'error') delete this.root.dataset.rseatFeedback;
    }

    showStatus(message, { type = 'status', timeout = 0 } = {}) {
      if (!message) return;
      const conciseMessage = this.getConciseMessage(message);
      window.clearTimeout(this.feedbackTimer);
      this.clearError();
      if (this.statusElement) {
        this.statusElement.textContent = conciseMessage;
        this.statusElement.dataset.type = type;
        this.statusElement.hidden = false;
      }
      if (this.mobileFeedback) {
        this.mobileFeedback.textContent = conciseMessage;
        this.mobileFeedback.dataset.type = type;
        this.mobileFeedback.setAttribute('role', 'status');
        this.mobileFeedback.setAttribute('aria-live', 'polite');
        this.mobileFeedback.hidden = false;
      }
      this.root.dataset.rseatFeedback = type;

      if (timeout > 0) {
        this.feedbackTimer = window.setTimeout(() => this.clearStatus(), timeout);
      }
    }

    clearStatus() {
      if (this.statusElement) {
        this.statusElement.textContent = '';
        this.statusElement.hidden = true;
        delete this.statusElement.dataset.type;
      }
      if (this.mobileFeedback?.dataset.type !== 'error') {
        this.mobileFeedback.textContent = '';
        this.mobileFeedback.hidden = true;
        delete this.mobileFeedback.dataset.type;
      }
      if (this.root.dataset.rseatFeedback !== 'error') delete this.root.dataset.rseatFeedback;
    }

    getAddedMessage(variants) {
      const count = variants.length;
      const labels = variants.map((variant) => variant.label).join(', ');
      const noun = count === 1 ? 'entrada agregada' : 'entradas agregadas';
      return `${count} ${noun}${labels ? `: ${labels}` : ''}.`;
    }

    getCartErrorMessage(state) {
      const rawMessage = state?.description || state?.message || state?.errors;
      if (typeof rawMessage === 'string') return this.getConciseMessage(rawMessage);
      if (rawMessage && typeof rawMessage === 'object') {
        const firstMessage = Object.values(rawMessage).flat().find((value) => typeof value === 'string');
        if (firstMessage) return this.getConciseMessage(firstMessage);
      }
      return this.root.dataset.errorLabel;
    }

    getConciseMessage(message) {
      const normalized = String(message || '').replace(/\s+/g, ' ').trim();
      if (normalized.length <= 180) return normalized;
      return `${normalized.slice(0, 177).trimEnd()}…`;
    }

    startCartLinesEvent(items) {
      const CartLinesUpdateEvent = window.StandardEvents?.CartLinesUpdateEvent;
      if (!CartLinesUpdateEvent || !this.form) return null;

      const deferred = CartLinesUpdateEvent.createPromise();
      this.form.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
          promise: deferred.promise,
        })
      );
      return deferred;
    }

    async refreshCartAfterAdd(deferred, addedIds = []) {
      const requestedIds = new Set(addedIds.map(String));
      const pollingDelays = [0, 300, 700, 1400, 2400];
      let eventResolved = false;
      let serviceChargeDetected = false;

      try {
        for (let attempt = 0; attempt < pollingDelays.length; attempt += 1) {
          const delay = pollingDelays[attempt];
          if (delay > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, delay));
          }

          const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
          const separator = cartUrl.includes('?') ? '&' : '?';
          const response = await fetch(`${cartUrl}${separator}_rseat=${Date.now()}-${attempt}`, {
            cache: 'no-store',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error('Cart refresh failed');
          const cart = await response.json();

          const CartLinesUpdateEvent = window.StandardEvents?.CartLinesUpdateEvent;
          if (!eventResolved && deferred && CartLinesUpdateEvent && cart?.currency) {
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
            eventResolved = true;
          }

          const seatLines = (cart.items || [])
            .map((item) => {
              const id = String(item.variant_id ?? item.id ?? '');
              const variant = this.variantById.get(id);
              if (!variant) return null;
              const quantity = Math.max(1, Number(item.quantity) || 1);
              const base = Number(variant.price || 0) * quantity;
              const finalPrice = Number(item.final_line_price ?? item.line_price ?? base);
              return { id, serviceCharge: Math.max(0, finalPrice - base) };
            })
            .filter(Boolean);

          if (seatLines.some((line) => line.serviceCharge > 0)) {
            serviceChargeDetected = true;
          }

          const requestedLines = seatLines.filter((line) => requestedIds.has(line.id));
          const pendingChargeIds = serviceChargeDetected
            ? requestedLines.filter((line) => line.serviceCharge <= 0).map((line) => line.id)
            : [];

          await this.syncCartSeats(cart, {
            reason: 'after-add',
            serviceChargePendingIds: pendingChargeIds,
          });

          const allRequestedLinesFound = requestedLines.length === requestedIds.size;
          const allChargesReady =
            serviceChargeDetected &&
            allRequestedLinesFound &&
            requestedLines.every((line) => line.serviceCharge > 0);

          if (allChargesReady) break;
        }
      } catch (error) {
        // El alta ya se completó. Este fallo sólo afecta la actualización
        // secundaria de analítica/estado y no debe mostrarse como error de compra.
        if (!eventResolved) deferred?.reject?.(error);
        this.scheduleCartSync(1200, 'after-add-retry');
      }
    }

    rejectCartLinesEvent(deferred, error) {
      deferred?.reject?.(error);
    }

    dispatchCartError(error) {
      const CartErrorEvent = window.StandardEvents?.CartErrorEvent;
      if (!CartErrorEvent || !this.form) return;
      this.form.dispatchEvent(
        new CartErrorEvent({
          error: error.message || this.root.dataset.errorLabel,
          code: error.code || 'SERVICE_UNAVAILABLE',
        })
      );
    }

    formatMoney(cents) {
      return this.money.format((Number(cents) || 0) / 100);
    }

    escapeSelector(value) {
      if (window.CSS?.escape) return window.CSS.escape(value);
      return String(value).replace(/(["\\])/g, '\\$1');
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(SELECTORS.root).forEach((root) => {
      if (root.dataset.rseatInitialized === 'true') return;
      root.dataset.rseatInitialized = 'true';
      root.rseatInstance = new RevibrasSeatSelector(root);
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SELECTORS.root).forEach((root) => {
      root.rseatInstance?.destroy?.();
      delete root.rseatInstance;
    });
  });
})();
